import { DEFAULT_LOCALE, interpolate, isLocale, type Locale } from '@hm/contracts'
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

/**
 * Dates read the language off the document rather than taking it as an argument.
 *
 * `<html lang>` is already the single source of truth — the provider writes it, and
 * a blocking script in index.html sets it before first paint — so threading a
 * locale through forty-odd call sites would add a parameter that could only ever
 * disagree with it. The parameter is still there for tests, which have no document
 * worth speaking of.
 */
function currentLocale(): Locale {
  const tag = globalThis.document?.documentElement.lang?.split('-')[0]
  return isLocale(tag) ? tag : DEFAULT_LOCALE
}

const RELATIVE: Record<Locale, { now: string; min: string; hr: string; days: string }> = {
  en: { now: 'just now', min: '{n} min ago', hr: '{n} hr ago', days: '{n} days ago' },
  ne: { now: 'भर्खरै', min: '{n} मिनेट अघि', hr: '{n} घण्टा अघि', days: '{n} दिन अघि' },
}

/** "2 hours ago" / "just now" — friendlier than a timestamp in a list. */
export function relativeTime(
  epochMs: number,
  now = Date.now(),
  locale: Locale = currentLocale(),
): string {
  const diff = now - epochMs
  const minute = 60_000
  const hour = 60 * minute
  const day = 24 * hour
  const words = RELATIVE[locale]

  if (diff < minute) return words.now
  if (diff < hour) return interpolate(words.min, { n: Math.floor(diff / minute) })
  if (diff < day) return interpolate(words.hr, { n: Math.floor(diff / hour) })
  if (diff < 7 * day) return interpolate(words.days, { n: Math.floor(diff / day) })
  return formatDate(epochMs, locale)
}

/**
 * Dates keep Arabic numerals and the short English month in both languages.
 *
 * Not an oversight: `ne-NP` would render Devanagari digits against a Gregorian
 * calendar, which is the worst of both — the audience reads Bikram Sambat dates in
 * Devanagari and Gregorian dates in Latin, and mixing the two is how "10 माघ" comes
 * to mean January. Until there is a real BS conversion this stays unambiguous.
 */
function formatDate(epochMs: number, _locale: Locale): string {
  return new Date(epochMs).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export function formatDateTime(epochMs: number): string {
  return new Date(epochMs).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}
